///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2026, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2026 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import type { IViewer } from "../viewer/IViewer";
import { IComponent, IComponentProvider, IComponentsRegistry } from "./IComponents";

class Components implements IComponentsRegistry {
  private readonly _providers = new Map<string, IComponentProvider>();

  registerComponent(name: string, provider: IComponentProvider): void {
    this._providers.set(name, provider);
  }

  registerComponentAlias(name: string, alias: string): void {
    const provider = this._providers.get(name);
    if (provider) this.registerComponent(alias, (viewer: IViewer) => provider(viewer));
  }

  getComponents(): Map<string, IComponentProvider> {
    const map = new Map<string, IComponentProvider>();
    this._providers.forEach((value, key) => map.set(key, value));
    return map;
  }

  createComponent(name: string, viewer: IViewer): IComponent | null {
    const provider = this._providers.get(name);
    if (!provider) return null;

    const component = provider(viewer);
    component.name = name;

    return component;
  }
}

const _components = new Map<string, Components>();

function componentsRegistry(viewerType = ""): IComponentsRegistry {
  let result = _components.get(viewerType);
  if (!result) {
    result = new Components();
    _components.set(viewerType, result);
  }
  return result;
}

export { componentsRegistry };
